import { ReactElement, useState, useMemo } from 'react';
import { CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';

import { DataResponse, DataPaginateResponse } from '~globals/types/api';
import { Nullable } from '~globals/types/commons';
import { getRoute } from '~services/route';
import { ApiTransactionRoutes } from '~services/apiTransaction/types';
import { getVehicles } from '~services/vehicle';
import { VehicleBase } from '~services/vehicle/types';
import { getDriverCompanyAvailables } from '~services/driver';
import { DriverCompanyBase } from '~services/driver/types';
import { useRequest } from '~hooks/useRequest';

import { DialogUpdateCompletedRouteProps } from './types';
import DialogUpdateCompletedRouteContent from './Content';
import {
  DialogUpdateCompletedRouteContainer,
  DialogUpdateCompletedRouteLoadingContainer,
} from './styles';

const DialogUpdateCompletedRoute = ({
  open,
  onClose,
  routeId,
  onSuccessSubmit,
}: DialogUpdateCompletedRouteProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [routeInfo, setRouteInfo] =
    useState<Nullable<ApiTransactionRoutes>>(null);
  const [, loadingRoute] = useRequest(
    {
      request: getRoute,
      payload: routeId,
      withPostSuccess: (response) => {
        const routeInfoResponse = response.data?.data as ApiTransactionRoutes;

        setRouteInfo(routeInfoResponse);
      },
      withPostFailure: () => {
        enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
          variant: 'error',
        });
      },
    },
    [],
  );

  const [drivers, setDrivers] = useState<DriverCompanyBase[]>([]);
  const [, loadingDrivers] = useRequest({
    request: getDriverCompanyAvailables,
    payload: null,
    withPostSuccess: (response) => {
      const availablesDriversResponse = response.data as DataPaginateResponse<
        DriverCompanyBase[]
      >;

      setDrivers(availablesDriversResponse.data.results);
    },
  });

  const [vehicles, setVehicles] = useState<VehicleBase[]>([]);
  const [, loadingVehicles] = useRequest({
    request: getVehicles,
    payload: null,
    withPostSuccess: (response) => {
      const vehiclesResponse = response.data as DataResponse<VehicleBase[]>;

      setVehicles(vehiclesResponse.data);
    },
  });

  const loading = useMemo(
    () => loadingRoute || loadingDrivers || loadingVehicles,
    [loadingRoute, loadingVehicles, loadingDrivers],
  );

  return (
    <DialogUpdateCompletedRouteContainer open={open}>
      {loading && (
        <DialogUpdateCompletedRouteLoadingContainer>
          <CircularProgress color="primary" disableShrink size={80} />
        </DialogUpdateCompletedRouteLoadingContainer>
      )}

      {!loading && routeInfo && (
        <DialogUpdateCompletedRouteContent
          onClose={onClose}
          routeInfo={routeInfo}
          vehicles={vehicles}
          drivers={drivers}
          onSuccessSubmit={onSuccessSubmit}
        />
      )}
    </DialogUpdateCompletedRouteContainer>
  );
};

export default DialogUpdateCompletedRoute;
